// services/api/chatbot/chatbot.hooks.ts
import { queryClient } from "@/src/config/react-query";
import { useAuth } from "@/src/contexts/auth.context";
import { useMutation, useQuery } from "@tanstack/react-query";
import apiClient from "../axios";
import gqlClient from "../graphql.client";
import {
  GET_ORG_CHATBOT_CONFIG,
  UPDATE_ORG_CHATBOT_CONFIG,
} from "./chatbot.query";

const BLOCKS_AI_URL = process.env.EXPO_PUBLIC_BLOCKS_BASE_URL;
const PROJECT_KEY = process.env.EXPO_PUBLIC_X_BLOCKS_KEY as string;

// Create agent
export const useCreateAgent = () => {
  return useMutation({
    mutationFn: async (payload: { name: string }) => {
      const res = await apiClient.post(
        `${BLOCKS_AI_URL}/blocksai-api/v1/agents/create`,
        {
          name: payload.name,
          description: "Chatbot for wiki articles",
          project_key: PROJECT_KEY,
          is_any_agent: false,
        },
      );
      return res.data;
    },
  });
};

// Create knowledge base folder
export const useCreateKbFolder = () => {
  return useMutation({
    mutationFn: async (payload: { name: string }) => {
      const res = await apiClient.post(
        `${BLOCKS_AI_URL}/blocksai-api/v1/kb/folder/create`,
        {
          project_key: PROJECT_KEY,
          name: payload.name,
          description: "KB for wiki articles",
          embedding_model_config: {
            Provider: "azure",
            Model: "text-embedding-ada-002",
            ChunkingStrategy: "semantic",
            IsProviderBlocks: true,
          },
        },
      );
      return res.data;
    },
  });
};

export const useGetAgentWidgetId = (id: string) => {
  return useQuery({
    queryKey: ["agentConfig", id],
    queryFn: async () => {
      const res = await apiClient.post(
        `${BLOCKS_AI_URL}/blocksai-api/v1/agents/queries`,
        {
          id: id,
          project_key: PROJECT_KEY,
        },
      );
      return res.data;
    },
    enabled: !!id,
  });
};

// Get org chatbot config from gateway
export const useGetChatbotConfig = () => {
  const { orgId } = useAuth();
  return useQuery({
    queryKey: ["chatbotConfig", orgId],
    queryFn: async () => {
      if (!orgId) throw new Error("orgId is required");

      const client = await gqlClient();
      const result = await client.request(GET_ORG_CHATBOT_CONFIG, {
        orgId: orgId!,
      });
      return result.getorganizationconfigs?.items?.[0]?.chatbotConfig ?? null;
    },
    enabled: !!orgId,
  });
};

// Save chatbot config to org
export const useUpdateChatbotConfig = () => {
  return useMutation({
    mutationFn: async (config: {
      orgId: string;
      itemId: string;
      kbFolderId: string;
      widgetId: string;
    }) => {
      const client = await gqlClient();
      return client.request(UPDATE_ORG_CHATBOT_CONFIG, config);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chatbotConfig"] });
    },
  });
};
